import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { identifyAnalyticsUser, isOpenPanelConfigured, setAnalyticsMarket } from '@/lib/analytics/openpanel';
import { ANALYTICS_PROFILE_COLUMNS, identityTraits, type AnalyticsIdentityRow } from '@/lib/analytics/identity';
import { hasAnalyticsConsent } from '@/lib/analyticsConsent';
import { logger } from '@/lib/logger';

/**
 * The one place the signed-in person is described to analytics.
 *
 * Runs once per session (user id change, or consent granted later in the
 * same session), reads the coarse columns of the person's own profile row
 * and sends identify() plus the market group. Restored sessions never pass
 * through /post-auth, which is why this lives in the app shell rather than
 * in the sign-in redirect.
 *
 * What is sent: role, helper status, market (country code, city), account
 * age bucket. What is never sent: name, email, phone, address, any id
 * document field. The column list and the trait shaping live in
 * src/lib/analytics/identity.ts, which openpanelConsentGuard.test.ts pins.
 */
export function useAnalyticsIdentity(): void {
  const { user } = useAuth();
  const userId = user?.id ?? null;

  useEffect(() => {
    if (!userId || !isOpenPanelConfigured()) return;
    let cancelled = false;

    const describe = async () => {
      if (!hasAnalyticsConsent()) return;
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select(ANALYTICS_PROFILE_COLUMNS)
          .eq('id', userId)
          .maybeSingle();
        if (cancelled) return;
        if (error) throw error;
        const row = (data ?? null) as AnalyticsIdentityRow | null;
        identifyAnalyticsUser(userId, identityTraits(row));
        if (row?.country_code) setAnalyticsMarket(row.country_code);
      } catch (error) {
        logger.warn('Analytics identity lookup failed', error);
      }
    };

    void describe();
    // Consent granted after sign-in (the banner answered later in the
    // session): describe the person then, not on the next login.
    const onConsent = (event: Event) => {
      if ((event as CustomEvent<{ analytics?: boolean }>).detail?.analytics === true) void describe();
    };
    window.addEventListener('cookieConsentChanged', onConsent);
    return () => {
      cancelled = true;
      window.removeEventListener('cookieConsentChanged', onConsent);
    };
  }, [userId]);
}
